import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { StudentSISPanel } from "../../components/StudentSISPanel";
import { Search, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export default function AdminSIS() {
  const { lang } = useLang();
  const t = (fr, en) => (lang === "fr" ? fr : en);
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get("/admin/sis/students")
      .then(r => { setStudents(r.data); if (r.data[0]) setSelected(r.data[0].id); })
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const filtered = students.filter(s => {
    const q = query.toLowerCase();
    if (!q) return true;
    return (`${s.first_name} ${s.last_name}`.toLowerCase().includes(q) ||
            (s.family?.email || "").toLowerCase().includes(q) ||
            (s.grade || "").toLowerCase().includes(q));
  });

  return (
    <div data-testid="admin-sis">
      <p className="overline mb-1">OFA France · Admin</p>
      <h1 className="font-display font-bold text-3xl tracking-tighter mb-6">
        {t("SIS · Vie scolaire", "SIS · Student Life")}
      </h1>

      <div className="grid lg:grid-cols-[300px_1fr] gap-6">
        {/* Sidebar */}
        <div className="space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="sis-search"
              placeholder={t("Rechercher un élève…", "Search a student…")}
              className="pl-9"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="sis-student-list">
            {filtered.length === 0 && (
              <p className="text-xs text-muted-foreground p-4 text-center">{t("Aucun élève inscrit pour l'instant.", "No enrolled students yet.")}</p>
            )}
            {filtered.map(s => (
              <button
                key={s.id}
                data-testid={`sis-student-${s.id}`}
                onClick={() => setSelected(s.id)}
                className={`w-full text-left border rounded-lg p-3 transition-colors ${selected === s.id ? "border-primary bg-primary/5" : "border-border hover:bg-secondary/50"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-sm">{s.first_name} {s.last_name}</div>
                  {s.unread_messages > 0 && (
                    <Badge className="bg-accent text-accent-foreground text-[10px] px-1.5 py-0">
                      <MessageCircle className="w-3 h-3 mr-0.5" />{s.unread_messages}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{s.grade || "—"} · {s.family?.email}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Detail */}
        <div>
          {selected ? (
            <StudentSISPanel studentId={selected} editable />
          ) : (
            <Card><CardContent className="p-12 text-center text-muted-foreground">
              {t("Sélectionnez un élève pour gérer ses notes, son emploi du temps et ses bulletins.",
                 "Select a student to manage grades, schedule and report cards.")}
            </CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}
